import * as React from 'react'
import { useState, useCallback, useEffect, useRef } from 'react'
import styled, { keyframes } from 'styled-components'
import { screenSizes } from '@src/styles/theme'
import { JsonSchema, ConfigUiHints } from '../types'
import ConfigCategory from './ConfigCategory'
import ConflictDialog from './ConflictDialog'

// Preferred display order, mirroring the section metadata from
// equabot-equa-app/ui/src/ui/views/config-form.render.ts (SECTION_META).
// Any schema property not listed here is appended alphabetically.
const PREFERRED_SECTION_ORDER = [
  'gateway',
  'auth',
  'models',
  'agents',
  'channels',
  'messages',
  'commands',
  'hooks',
  'skills',
  'tools',
  'plugins',
  'session',
  'browser',
  'cron',
  'audio',
  'logging',
  'env',
  'update',
  'meta',
  'bindings',
  'broadcast',
  'discovery',
  'canvasHost',
  'talk',
  'ui',
  'web',
]

// Top-level schema properties that are infra/internal and should be hidden
// from the config editor even if the gateway returns them in the schema.
const HIDDEN_SECTIONS = new Set<string>(['wizard'])

type Props = {
  config: Record<string, unknown>
  schema: JsonSchema
  uiHints: ConfigUiHints
  baseHash: string
  onSave: (patch: Record<string, unknown>) => Promise<void>
  onRefresh: () => Promise<void>
  loading?: boolean
  /** Optional scope label displayed above the section list (e.g. "Personal account", "Acme Corp"). */
  scopeLabel?: string
  /** Optional banner message shown above the editor (e.g. "Org-scoped config not yet supported"). */
  scopeNotice?: string
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-width: 100%;
  overflow-x: hidden;
`

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0 12px;
  gap: 12px;
  flex-wrap: wrap;

  @media (max-width: ${screenSizes.XS}px) {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }
`

const DirtyLabel = styled.span`
  color: ${(p) => p.theme.warning};
  font-size: 13px;
  font-family: 'NunitoSansSemiBold';
`

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;

  @media (max-width: ${screenSizes.XS}px) {
    flex-direction: column;
  }
`

const SaveButton = styled.button`
  padding: 10px 24px;
  border: none;
  border-radius: ${(p) => p.theme.radius};
  font-size: 14px;
  font-family: 'NunitoSansBold';
  cursor: pointer;
  color: ${(p) => p.theme.white};
  height: 44px;
  background-color: ${(p) => p.theme.button.primary.default};

  &:hover {
    background-color: ${(p) => p.theme.button.primary.hover};
  }

  &:disabled {
    background-color: ${(p) => p.theme.button.primary.disabled};
    cursor: not-allowed;
  }

  &:focus-visible {
    box-shadow: 0 0 0 2px ${(p) => p.theme.white}, 0 0 0 4px ${(p) => p.theme.accent};
    outline: none;
  }
`

const DiscardButton = styled.button`
  padding: 10px 24px;
  border: none;
  border-radius: ${(p) => p.theme.radius};
  font-size: 14px;
  font-family: 'NunitoSansBold';
  cursor: pointer;
  color: ${(p) => p.theme.white};
  height: 44px;
  background-color: ${(p) => p.theme.button.cancel.default};

  &:hover {
    background-color: ${(p) => p.theme.button.cancel.hover};
  }

  &:focus-visible {
    box-shadow: 0 0 0 2px ${(p) => p.theme.accent};
    outline: none;
  }
`

const StatusBanner = styled.div<{ $variant: 'success' | 'error' }>`
  padding: 10px 14px;
  border-radius: ${(p) => p.theme.radius};
  font-size: 13px;
  font-family: 'NunitoSansSemiBold';
  color: ${(p) => p.theme.white};
  background-color: ${(p) =>
    p.$variant === 'success' ? p.theme.button.primary.default : p.theme.error};
  margin-bottom: 8px;
  word-break: break-word;
`

const ScopeBanner = styled.div`
  padding: 12px 16px;
  border-radius: ${(p) => p.theme.radius};
  background-color: ${(p) => p.theme.inputBack};
  border-left: 4px solid ${(p) => p.theme.accent};
  margin-bottom: 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const ScopeBannerLabel = styled.span`
  color: ${(p) => p.theme.listDesc};
  font-size: 11px;
  font-family: 'NunitoSansSemiBold';
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const ScopeBannerValue = styled.span`
  color: ${(p) => p.theme.white};
  font-size: 16px;
  font-family: 'NunitoSansBold';
`

const ScopeBannerNotice = styled.span`
  color: ${(p) => p.theme.warning};
  font-size: 12px;
  font-family: 'NunitoSans';
  margin-top: 4px;
`

const pulse = keyframes`
  0%, 100% { opacity: 0.35; }
  50% { opacity: 0.6; }
`

const SkeletonBlock = styled.div`
  height: 56px;
  background-color: ${(p) => p.theme.inputBack};
  border-radius: ${(p) => p.theme.radius};
  animation: ${pulse} 1.5s ease-in-out infinite;
`

/**
 * Immutably set a value at a nested path, handling both objects and arrays.
 */
function setDeep(node: unknown, path: Array<string | number>, value: unknown): unknown {
  if (path.length === 0) return value
  const [head, ...rest] = path
  if (Array.isArray(node)) {
    const copy = [...node]
    const idx = typeof head === 'number' ? head : parseInt(String(head), 10)
    copy[idx] = setDeep(copy[idx], rest, value)
    return copy
  }
  const obj =
    typeof node === 'object' && node !== null
      ? { ...(node as Record<string, unknown>) }
      : ({} as Record<string, unknown>)
  obj[String(head)] = setDeep(obj[String(head)], rest, value)
  return obj
}

function setAtPath(
  root: Record<string, unknown>,
  path: Array<string | number>,
  value: unknown
): Record<string, unknown> {
  return setDeep(root, path, value) as Record<string, unknown>
}

function isBaseHashConflict(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err)
  return msg.toLowerCase().includes('config changed') || msg.includes('INVALID_REQUEST')
}

export default function ConfigEditor({ config, schema, uiHints, baseHash, onSave, onRefresh, loading, scopeLabel, scopeNotice }: Props) {
  const [draft, setDraft] = useState<Record<string, unknown>>(config)
  const [saving, setSaving] = useState(false)
  const [showConflict, setShowConflict] = useState(false)
  const [banner, setBanner] = useState<{ variant: 'success' | 'error'; message: string } | null>(null)
  const bannerTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    setDraft(config)
  }, [config])

  useEffect(() => {
    return () => {
      if (bannerTimer.current) clearTimeout(bannerTimer.current)
    }
  }, [])

  const isDirty = JSON.stringify(draft) !== JSON.stringify(config)

  const showBanner = useCallback((variant: 'success' | 'error', message: string) => {
    setBanner({ variant, message })
    if (bannerTimer.current) clearTimeout(bannerTimer.current)
    bannerTimer.current = setTimeout(() => setBanner(null), 4000)
  }, [])

  const handleChange = useCallback((path: Array<string | number>, value: unknown) => {
    setDraft((prev) => setAtPath(prev, path, value))
  }, [])

  const handleSave = useCallback(async () => {
    setSaving(true)
    setBanner(null)
    try {
      await onSave(draft)
      showBanner('success', 'Configuration saved successfully.')
    } catch (err) {
      if (isBaseHashConflict(err)) {
        setShowConflict(true)
      } else {
        showBanner('error', err instanceof Error ? err.message : 'Failed to save configuration.')
      }
    } finally {
      setSaving(false)
    }
  }, [draft, onSave, showBanner])

  const handleDiscard = useCallback(() => {
    setDraft(config)
    setBanner(null)
  }, [config])

  const handleConflictReload = useCallback(async () => {
    setShowConflict(false)
    setBanner(null)
    await onRefresh()
  }, [onRefresh])

  const handleConflictCancel = useCallback(() => {
    setShowConflict(false)
  }, [])

  const visibleCategories = React.useMemo(() => {
    const props = schema.properties ?? {}
    const keys = Object.keys(props).filter((key) => {
      if (HIDDEN_SECTIONS.has(key)) return false
      const prop = props[key] as { type?: string } | undefined
      // Only render object-shaped top-level sections; scalars don't form a category view.
      if (!prop) return false
      if (prop.type && prop.type !== 'object') return false
      return true
    })
    return keys.sort((a, b) => {
      const ai = PREFERRED_SECTION_ORDER.indexOf(a)
      const bi = PREFERRED_SECTION_ORDER.indexOf(b)
      if (ai === -1 && bi === -1) return a.localeCompare(b)
      if (ai === -1) return 1
      if (bi === -1) return -1
      return ai - bi
    })
  }, [schema])

  if (loading) {
    return (
      <Wrapper>
        <SkeletonBlock />
        <SkeletonBlock style={{ animationDelay: '0.15s' }} />
        <SkeletonBlock style={{ animationDelay: '0.3s' }} />
      </Wrapper>
    )
  }

  return (
    <Wrapper>
      {banner && <StatusBanner $variant={banner.variant}>{banner.message}</StatusBanner>}

      {scopeLabel && (
        <ScopeBanner>
          <ScopeBannerLabel>Configuring</ScopeBannerLabel>
          <ScopeBannerValue>{scopeLabel}</ScopeBannerValue>
          {scopeNotice && <ScopeBannerNotice>{scopeNotice}</ScopeBannerNotice>}
        </ScopeBanner>
      )}

      {isDirty && (
        <Toolbar>
          <DirtyLabel>Unsaved changes</DirtyLabel>
          <ButtonGroup>
            <DiscardButton type="button" onClick={handleDiscard}>Discard</DiscardButton>
            <SaveButton type="button" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving\u2026' : 'Save'}
            </SaveButton>
          </ButtonGroup>
        </Toolbar>
      )}

      {visibleCategories.map((key) => (
        <ConfigCategory
          key={key}
          categoryKey={key}
          schema={schema}
          config={draft}
          hints={uiHints}
          disabled={saving}
          onChange={handleChange}
        />
      ))}

      {visibleCategories.length === 0 && (
        <StatusBanner $variant="error">
          No configurable sections available. The connected Gateway did not return any editable sections in its schema.
        </StatusBanner>
      )}

      <ConflictDialog
        show={showConflict}
        onReload={handleConflictReload}
        onCancel={handleConflictCancel}
      />
    </Wrapper>
  )
}
